import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, WMSTileLayer, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import axios from 'axios';
import { Plus, Minus } from 'lucide-react';
import { API_URL } from '../config';
import MapLayerControl, { MapLayerType } from './MapLayerControl';

interface PublicMapProps {
    slug: string;
}

const CustomZoomControl = () => {
    const map = useMap();
    return (
        <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 1000, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div className="public-zoom-btn-container" onClick={() => map.zoomIn()}>
                <div className="public-zoom-btn">
                    <Plus size={20} color="white" strokeWidth={2.5} />
                </div>
            </div>
            <div className="public-zoom-btn-container" onClick={() => map.zoomOut()}>
                <div className="public-zoom-btn">
                    <Minus size={20} color="white" strokeWidth={2.5} />
                </div>
            </div>
        </div>
    );
};

const PublicMapEvents = ({ onZoomChange }: { onZoomChange: (z: number) => void }) => {
    const map = useMapEvents({
        zoomend: () => onZoomChange(map.getZoom())
    });
    return null;
};

const PublicMap: React.FC<PublicMapProps> = ({ slug }) => {
    const [data, setData] = useState<{ org: any, pipes: any } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [zoomLevel, setZoomLevel] = useState<number>(12);
    const [mapLayer, setMapLayer] = useState<MapLayerType>(() => {
        return (localStorage.getItem('aquaflow_map_layer') as MapLayerType) || 'osm';
    });

    const handleMapLayerChange = (layer: MapLayerType) => {
        setMapLayer(layer);
        localStorage.setItem('aquaflow_map_layer', layer);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${API_URL}/public/map/${slug}`);
                setData(res.data);
            } catch (err: any) {
                setError(err.response?.data?.detail || "Nie udało się załadować mapy. Sprawdź poprawność linku.");
            }
        };
        fetchData();
    }, [slug]);

    if (error) {
        return (
            <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', color: '#ef4444', fontWeight: 'bold' }}>
                {error}
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                Ładowanie mapy...
            </div>
        );
    }

    const mapCenter: [number, number] = [data.org.latitude, data.org.longitude];

    return (
        <div style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>
            <MapContainer
                center={mapCenter}
                zoom={12}
                minZoom={12}
                maxZoom={22}
                zoomControl={false}
                style={{ height: '100%', width: '100%' }}
            >
                {mapLayer === 'osm' && (
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        maxNativeZoom={19}
                        maxZoom={22}
                    />
                )}
                {mapLayer === 'geoportal' && (
                    <>
                        <TileLayer
                            url="/tiles/{z}/{x}/{y}.jpg"
                            attribution='&copy; Główny Urząd Geodezji i Kartografii (Lokalna Ortomapa)'
                            minZoom={12}
                            maxNativeZoom={18}
                            maxZoom={22}
                        />
                        {zoomLevel >= 19 && (
                            <WMSTileLayer
                                url="https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/HighResolution"
                                layers="Raster"
                                format="image/jpeg"
                                attribution='&copy; Główny Urząd Geodezji i Kartografii (GUGiK Live)'
                                minZoom={19}
                                maxNativeZoom={20}
                                maxZoom={22}
                                transparent={true}
                            />
                        )}
                    </>
                )}
                {mapLayer === 'satellite' && (
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                        maxNativeZoom={19}
                        maxZoom={22}
                    />
                )}
                <CustomZoomControl />
                <PublicMapEvents onZoomChange={setZoomLevel} />

                <GeoJSON
                    key={`p-${JSON.stringify(data.pipes)}`}
                    data={data.pipes}
                    style={(f: any) => ({
                        color: f?.properties.status === 'open' ? (f?.properties.color || '#3b82f6') : '#ef4444',
                        weight: 4,
                        opacity: 0.85,
                        dashArray: f?.properties.is_sp ? '10, 10' : undefined
                    })}
                />
            </MapContainer>

            {/* Przełącznik podkładu mapy */}
            <MapLayerControl currentLayer={mapLayer} onLayerChange={handleMapLayerChange} />

            <div style={{ position: 'absolute', bottom: '20px', left: '20px', zIndex: 1000, backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '6px 12px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', fontSize: '13px', color: '#4b5563' }}>
                <strong style={{ color: '#111827' }}>{data.org.name}</strong> - Sieć Wodociągowa
            </div>
        </div>
    );
};

export default PublicMap;
